/**
 * Parser del Markdown acotado con el que se escribe el resumen de contenido de un
 * módulo (`ContentSection.content`). Es el mismo subconjunto que genera el seed del
 * backend (`seed-data/content/`), a propósito sin dependencias: el conjunto es chico
 * y así se controla exactamente qué se renderiza (nada de HTML).
 *
 * Subconjunto:
 * - Párrafos: líneas seguidas se unen; una línea en blanco separa párrafos.
 * - Subtítulos: `#`/`##` (subtítulo principal), `###` (subtítulo) y `####` (menor); el nivel
 *   define la jerarquía visual dentro de la sección.
 * - Listas: renglones que empiezan con `- ` o `* `; la continuación indentada se une al ítem.
 * - Enumeraciones: renglones que empiezan con su marcador (`a. `, `b) `, `1. `, `12) `). Se muestran
 *   sin viñeta y con el marcador textual, para que se lean igual que en el material del curso.
 * - Imágenes: `![leyenda](url "size=sm|md|lg|full")`, cada una en su renglón. Varias en renglones
 *   consecutivos forman una galería. El título admite otras notas (`pág. 12`), que se ignoran.
 * - En línea: `**negrita**` y `*cursiva*`.
 */

export type ImageSize = "sm" | "md" | "lg" | "full";

export interface MarkdownImage {
  url: string;
  caption: string;
  size: ImageSize;
}

export type MarkdownBlock =
  | { kind: "heading"; text: string; level: 1 | 2 | 3 | 4 }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "orderedList"; items: { marker: string; text: string }[] }
  | { kind: "image"; image: MarkdownImage }
  | { kind: "gallery"; images: MarkdownImage[] };

export type InlineNode =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "em"; text: string };

const IMAGE_LINE = /^!\[([^\]]*)\]\(([^\s)]+)(?:\s+"([^"]*)")?\)\s*$/;
const HEADING_LINE = /^(#{1,4})\s+(.+?)\s*$/;
const LIST_ITEM = /^[-*]\s+(.*)$/;
const ENUM_ITEM = /^((?:[a-z]|[0-9]{1,2})[.)])\s+(.*)$/;
const SIZES: ImageSize[] = ["sm", "md", "lg", "full"];
const DEFAULT_SIZE: ImageSize = "md";

const parseImage = (match: RegExpExecArray): MarkdownImage => {
  const title = match[3] ?? "";
  const sizeToken = title
    .split(/\s+/)
    .map((t) => /^size=(\w+)$/.exec(t)?.[1])
    .find((s): s is ImageSize => SIZES.includes(s as ImageSize));
  return {
    url: match[2],
    caption: match[1].trim(),
    size: sizeToken ?? DEFAULT_SIZE
  };
};

/**
 * Convierte el Markdown acotado en bloques listos para renderizar. Nunca lanza:
 * lo que no matchea ninguna regla es texto de párrafo.
 */
export const parseMarkdown = (markdown: string): MarkdownBlock[] => {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] | null = null;
  let enumList: { marker: string; text: string }[] | null = null;
  let images: MarkdownImage[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list) {
      blocks.push({ kind: "list", items: list });
      list = null;
    }
    if (enumList) {
      blocks.push({ kind: "orderedList", items: enumList });
      enumList = null;
    }
  };
  const flushImages = () => {
    if (images.length === 1) blocks.push({ kind: "image", image: images[0] });
    else if (images.length > 1) blocks.push({ kind: "gallery", images });
    images = [];
  };
  const flushAll = () => {
    flushParagraph();
    flushList();
    flushImages();
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushAll();
      continue;
    }
    const image = IMAGE_LINE.exec(line);
    if (image) {
      flushParagraph();
      flushList();
      images.push(parseImage(image));
      continue;
    }
    // Cualquier otra línea corta una galería en curso.
    flushImages();

    const heading = HEADING_LINE.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        kind: "heading",
        text: heading[2],
        level: heading[1].length as 1 | 2 | 3 | 4
      });
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      // Abrir una lista cierra lo que hubiera pendiente (párrafo o enumeración).
      if (!list) {
        flushParagraph();
        flushList();
      }
      list = list ?? [];
      list.push(item[1].trim());
      continue;
    }
    const enumItem = ENUM_ITEM.exec(line);
    if (enumItem) {
      if (!enumList) {
        flushParagraph();
        flushList();
      }
      enumList = enumList ?? [];
      enumList.push({ marker: enumItem[1], text: enumItem[2].trim() });
      continue;
    }
    if (list) {
      // Continuación de un ítem (línea indentada o texto seguido).
      list[list.length - 1] += ` ${line.trim()}`;
      continue;
    }
    if (enumList) {
      enumList[enumList.length - 1].text += ` ${line.trim()}`;
      continue;
    }
    paragraph.push(line.trim());
  }
  flushAll();
  return blocks;
};

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;

/** Parte un texto en nodos de texto, negrita (`**x**`) y cursiva (`*x*`). */
export const parseInline = (text: string): InlineNode[] => {
  const nodes: InlineNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0;
    if (index > last)
      nodes.push({ kind: "text", text: text.slice(last, index) });
    const token = match[0];
    if (token.startsWith("**")) {
      nodes.push({ kind: "strong", text: token.slice(2, -2) });
    } else {
      nodes.push({ kind: "em", text: token.slice(1, -1) });
    }
    last = index + token.length;
  }
  if (last < text.length) nodes.push({ kind: "text", text: text.slice(last) });
  return nodes;
};

/** Línea Markdown de una imagen, para insertarla desde el editor. */
export const imageMarkdown = (
  url: string,
  size: ImageSize = DEFAULT_SIZE,
  caption = ""
): string => `![${caption}](${url} "size=${size}")`;
